import { css } from '@emotion/react'
import styled from '@emotion/styled'
import { Image, Props as ImageProps } from 'app/components/Common/Image'
import { styles } from 'app/theme'
import { useParallax } from 'app/utils/customFunctions'
import { motion, useScroll } from 'framer-motion'
import React, { memo, useRef } from 'react'

export interface Props {
  image: ImageProps
  title?: string
  variant?: Variant
}

export const Slide = memo(function Slide({
  image,
  title,
  variant = 'default',
}: Props) {
  if (!image) {
    return null
  }

  const containerRef = useRef<any>()

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ['start end', '100vh'],
  })

  const y = useParallax(scrollYProgress, [-50, 50])

  return (
    <Container ref={containerRef} variant={variant}>
      <Background>
        <Inner style={{ y }}>
          <Image {...image} />
        </Inner>
      </Background>

      {title ? <Title>{title}</Title> : null}
    </Container>
  )
})

const Background = styled.div`
  aspect-ratio: 1.33;
  overflow: hidden;
`

const Inner = styled(motion.div)`
  height: calc(100% + 6.25rem);
  position: relative;

  img {
    ${styles.img}
  }

  @media (max-width: 1199px) {
    height: 100%;
    transform: none !important;
  }
`

const Title = styled.div`
  ${styles.description}

  color: ${({ theme }) => theme.colors.variants.neutralLight1};
  font-size: 0.875rem;
  line-height: 1.0625rem;
  margin-top: 1rem;
`

const Container = styled.div<ContainerProps>`
  margin-top: 7.5rem;
  &:first-of-type {
    margin-top: 0;
  }
  &:last-of-type {
    width: 38.889vw;
    ${Background} {
      aspect-ratio: 0.75;
    }
  }

  ${({ variant }) => {
    switch (variant) {
      case 'invert':
        return css`
          &:first-of-type {
            width: 38.889vw;
            margin-left: auto;
            ${Background} {
              aspect-ratio: 0.75;
            }
          }
          &:last-of-type {
            width: 100%;
            ${Background} {
              aspect-ratio: 1.33;
            }
          }

          @media (max-width: 1199px) {
            &:first-of-type {
              width: 100%;
              ${Background} {
                aspect-ratio: 1.33;
              }
            }
          }
        `
    }
  }}

  @media (max-width: 1199px) {
    margin-top: 2.625rem;
    &:first-of-type {
      margin-top: 2.625rem;
    }
    &:last-of-type {
      width: 100%;
      ${Background} {
        aspect-ratio: 1.33;
      }
    }
  }
`

interface ContainerProps {
  variant: Variant
}

export type Variant = 'default' | 'invert'
